import { styled } from "@mui/material/styles";
import {
    Box,
    Card,
    CardContent,
    GridProps,
    IconButton,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FallbackWaveform from "Images/Content/FallbackWaveform.png";
import { CourseStep } from "JS/Models";
import {
    DownloadStatusIcon,
    StepListDownloadType,
} from "JS/React/Components/Icon/DownloadStatusIcon";

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    display: "flex",
    alignItems: "center",
    cursor: "pointer !important",
    marginBottom: "15px",
    paddingLeft: "10px",
    paddingRight: "20px",
    position: "relative",
    "&:last-child": {
        marginBottom: "20px",
    },
}));

const StyledCardContent = styled(CardContent)(() => ({
    width: "75%",
    paddingRight: "20px",
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
    fontSize: "18px",
    marginBottom: "5px",
    color: theme.palette.grey[600],
}));

const SkuTypography = styled(Typography)(({ theme }) => ({
    fontSize: "14px",
    color: theme.palette.grey[500],
}));

const ClampTypography = styled(Typography)(({ theme }) => ({
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-all",
    overflow: "hidden",
    color: theme.palette.grey[600],
}));

const ActionsBox = styled(Box)(() => ({
    position: "absolute",
    top: "16px",
    right: "20px",
}));

const CompletedIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));

const Grey600Icon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const ImgLockIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    position: "absolute",
    color: theme.palette.grey[600],
}));

const PlayIcon = styled(FontAwesomeIcon, {
    shouldForwardProp: (prop) => prop !== "isResume",
})<{ isResume?: boolean }>(({ theme, isResume }) => ({
    color: isResume ? theme.palette.primary.main : theme.palette.grey[600],
}));

const StyledIconButton = styled(IconButton)(() => ({
    padding: 0,
}));

export const StepsListItem = (props: StepsListItemProps) => {
    const {
        isAllowClick,
        isLocked,
        isDisabled,
        stepThumbnail,
        isQuiz,
        step,
        isCompleted,
        isResume,
        downloadStatus,
        onListItemClick,
    } = props;

    const { handlePlayIconClick } = useHandlers(props);

    return (
        <StyledCard
            onClick={() => {
                if (isAllowClick && !isLocked && !isDisabled) {
                    onListItemClick();
                }
            }}
            elevation={0}
            sx={{
                ...(isLocked && { opacity: "80%" }),
                ...(isDisabled && { opacity: 0.6 }),
            }}
        >
            <Box
                sx={{
                    width: "80px",
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                }}
            >
                {isLocked && (
                    <ImgLockIcon
                        style={{ fontSize: "22px" }}
                        icon={["fas", "lock"]}
                    />
                )}
                <img
                    src={stepThumbnail || FallbackWaveform}
                    style={{ width: "60px", height: "auto" }}
                    alt="thumbnail"
                />
            </Box>
            <StyledCardContent>
                <TitleTypography>
                    {!isLocked && isCompleted && (
                        <CompletedIcon icon={["fas", "circle-check"]} />
                    )}
                    &nbsp;&nbsp;
                    {step.title || ""}
                </TitleTypography>
                <SkuTypography>
                    {step?.sku_id || ""} | {step?.content_type?.toUpperCase() || ""}
                </SkuTypography>
                <ClampTypography fontSize={"12px"}>
                    {step.description || ""}
                </ClampTypography>
            </StyledCardContent>
            <ActionsBox
                display={"flex"}
                alignItems={"center"}
                height={"100%"}
                flexDirection={"column"}
            >
                <>
                    {isLocked ? (
                        <Grey600Icon
                            style={{ fontSize: "22px" }}
                            icon={["fas", "lock"]}
                        />
                    ) : (
                        <>
                            {!isQuiz && (
                                <>
                                    {step.content_type !== "PDF" && (
                                        <StyledIconButton
                                            onClick={handlePlayIconClick}
                                        >
                                            <PlayIcon
                                                style={{
                                                    fontSize: "22px",
                                                }}
                                                isResume={isResume}
                                                icon={["fas", "play"]}
                                            />
                                        </StyledIconButton>
                                    )}
                                    <DownloadStatusIcon
                                        downloadStatus={downloadStatus}
                                    />
                                </>
                            )}
                        </>
                    )}
                </>
            </ActionsBox>
        </StyledCard>
    );
};

const useHandlers = (props: StepsListItemProps) => {
    const { onPlayIconClick } = props;

    const handlePlayIconClick = (e) => {
        if (onPlayIconClick) {
            e.stopPropagation();
            onPlayIconClick();
        }
    };
    return { handlePlayIconClick };
};

export interface StepsListItemProps extends InternalStandardProps<GridProps> {
    step: CourseStep;
    stepThumbnail: string;
    isAllowClick?: boolean;
    isLocked?: boolean;
    isDisabled?: boolean;
    isQuiz?: boolean;
    isCompleted?: boolean;
    isResume?: boolean;
    downloadStatus?: StepListDownloadType;
    onListItemClick?: () => void;
    onPlayIconClick?: () => void;
}
